// libraries
import { Typography } from '@interstate/components/Typography';

// types
import type { OpenRepairOrder } from '@makemydeal/dr-dash-types';

// components
import RepairOrderFieldUI from './RepairOrderFieldUI';

// utils
import {
    NO_SERVICES_MESSAGE,
    formatRepairOrderDate,
    formatRepairOrderNumber,
    formatRepairOrderTotal,
    formatServiceCost,
    formatServiceLabel,
    getServiceLineKey
} from './repairOrderDisplay';

// styles
import {
    StyledRepairOrderCard,
    StyledRepairOrderHeadline,
    StyledRepairOrderServiceRow,
    StyledRepairOrderServiceRowContent,
    StyledRepairOrderServices
} from './RepairOrders.style';

export type RepairOrderCardUIProps = {
    repairOrder: OpenRepairOrder;
    index: number;
};

export const RepairOrderCardUI = ({ repairOrder, index }: RepairOrderCardUIProps) => {
    const { lastUpdatedDate, total, services } = repairOrder;

    return (
        <StyledRepairOrderCard data-testid={`repair-order-card-${index}`}>
            <StyledRepairOrderHeadline>
                <Typography tag="span" variant="h6" color="sem.color.on-surface.interactive.rest">
                    {formatRepairOrderNumber(repairOrder, index)}
                </Typography>
                <Typography
                    tag="span"
                    variant="h6"
                    color="sem.color.on-surface.default"
                    data-testid={`repair-order-headline-date-${index}`}
                >
                    {`Last updated: ${formatRepairOrderDate(lastUpdatedDate)}`}
                </Typography>
            </StyledRepairOrderHeadline>

            <RepairOrderFieldUI
                label="RO Total"
                value={formatRepairOrderTotal(total)}
                data-testid={`repair-order-total-${index}`}
            />

            <Typography tag="span" variant="h6" color="sem.color.on-surface.default">
                Provided Services:
            </Typography>
            {services.length === 0 ? (
                <Typography
                    tag="span"
                    variant="body-sm"
                    color="sem.color.on-surface.default"
                    data-testid={`repair-order-no-services-${index}`}
                >
                    {NO_SERVICES_MESSAGE}
                </Typography>
            ) : (
                <StyledRepairOrderServices data-testid={`repair-order-services-${index}`}>
                    {services.map((service, serviceIndex) => (
                        <StyledRepairOrderServiceRow
                            key={getServiceLineKey(service, serviceIndex)}
                            data-testid={`repair-order-service-${index}-${serviceIndex}`}
                        >
                            <StyledRepairOrderServiceRowContent>
                                <Typography tag="span" variant="body-sm" color="sem.color.on-surface.default">
                                    {formatServiceLabel(service)}
                                </Typography>
                                <Typography tag="span" variant="body-sm" color="sem.color.on-surface.default">
                                    {formatServiceCost(service)}
                                </Typography>
                            </StyledRepairOrderServiceRowContent>
                        </StyledRepairOrderServiceRow>
                    ))}
                </StyledRepairOrderServices>
            )}
        </StyledRepairOrderCard>
    );
};

export default RepairOrderCardUI;
